import { z } from 'zod';

import { Summary, SummaryLike } from '@airreading/common';
import { entityTypeSchema } from './association.js';
import { Delta, deltaKindSchema, UserRoleCreationDelta, UserRoleDeletionDelta, UserRoleDelta } from './delta.js';

/**
 * An object ID originating from the external data source Edlink.
 */
export const edlinkIdSchema = z.string().brand<'edlinkId'>();
export const EdlinkId = edlinkIdSchema.parse.bind(edlinkIdSchema);
export type EdlinkId = z.infer<typeof edlinkIdSchema>;

export const edlinkDistrictEntrySchema = z.object({
  integrationId: edlinkIdSchema,
  districtId: edlinkIdSchema,
  districtName: z.string(),
});
export type EdlinkDistrictEntry = z.infer<typeof edlinkDistrictEntrySchema>;

/**
 * Extracts a human-readable message from an error value of unknown type.
 *
 * Error objects do not survive JSON serialization (JSON.stringify(new Error()) = "{}"),
 * so when failures are deserialized on the client the error field may arrive as a plain
 * object. This function handles all observed shapes:
 *   - Real Error instance: userFacingMessage or message
 *   - Deserialized { message: … }: message
 *   - String: the string itself
 *   - Anything else: "Unknown error"
 */
export function extractErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return (error as { userFacingMessage?: string }).userFacingMessage ?? error.message;
  }

  if (error != null && typeof error === 'object' && 'message' in error) {
    const msg = (error as { message: unknown }).message;

    if (typeof msg === 'string') {
      return msg;
    }
  }

  if (typeof error === 'string') {
    return error;
  }

  return 'Unknown error';
}

export const edlinkOrgChartImportSuccessSchema = z.object({
  type: entityTypeSchema,
  kind: deltaKindSchema,
  internalId: z.string().optional(),
  externalId: edlinkIdSchema.optional(),
  details: z.record(z.string(), z.string().nullable()).optional(),
});
export type EdlinkOrgChartImportSuccess = z.infer<typeof edlinkOrgChartImportSuccessSchema>;

export const edlinkOrgChartImportFailureSchema = z.object({
  type: entityTypeSchema,
  kind: deltaKindSchema,
  internalId: z.string().optional(),
  externalId: edlinkIdSchema.optional(),
  error: z.unknown(),
});
export type EdlinkOrgChartImportFailure = z.infer<typeof edlinkOrgChartImportFailureSchema>;

export const edlinkOrgChartImportSkipSchema = z.object({
  type: entityTypeSchema,
  kind: deltaKindSchema,
  internalId: z.string().optional(),
  externalId: edlinkIdSchema.optional(),
  reason: z.string(),
});
export type EdlinkOrgChartImportSkip = z.infer<typeof edlinkOrgChartImportSkipSchema>;

export class EdlinkOrgChartImportSummary extends Summary<
  EdlinkOrgChartImportSuccess,
  EdlinkOrgChartImportFailure,
  EdlinkOrgChartImportSkip
> {
  get items() {
    return this.results.map(({ type, kind, internalId, externalId, details }) => ({
      type,
      kind,
      internalId,
      externalId,
      label: labelForEdlinkResult(type, kind, internalId, externalId),
      details,
    }));
  }

  get failureItems() {
    return this.failures.map(({ type, kind, internalId, externalId, error }) => ({
      type,
      kind,
      internalId,
      externalId,
      label: labelForEdlinkResult(type, kind, internalId, externalId, 'failed'),
      details: {
        reason: extractErrorMessage(error),
      },
    }));
  }

  get skipItems() {
    return this.skips.map(({ type, kind, internalId, externalId, reason }) => ({
      type,
      kind,
      internalId,
      externalId,
      label: labelForEdlinkResult(type, kind, internalId, externalId, 'skipped'),
      details: { reason },
    }));
  }

  override toJSON(): SummaryLike<EdlinkOrgChartImportSuccess, EdlinkOrgChartImportFailure, EdlinkOrgChartImportSkip> {
    const base = super.toJSON();
    return {
      ...base,
      failures: this.failures.map(({ error, ...rest }) => ({
        ...rest,
        error: { message: extractErrorMessage(error) },
      })),
    };
  }
}
export type EdlinkOrgChartImportSummaryLike = SummaryLike<
  EdlinkOrgChartImportSuccess,
  EdlinkOrgChartImportFailure,
  EdlinkOrgChartImportSkip
>;

export const edlinkOrgChartDryRunItemSchema = z.object({
  delta: entityTypeSchema,
  kind: deltaKindSchema,
  label: z.string().optional(),
  details: z.record(z.string(), z.string()).optional(),
});
export type EdlinkOrgChartDryRunItem = z.infer<typeof edlinkOrgChartDryRunItemSchema>;
export type EdlinkOrgChartDryRunResult = {
  items: Delta<string, object>[];
  failures: EdlinkOrgChartDryRunItem[];
  skips: EdlinkOrgChartDryRunItem[];
};

export function labelForEdlinkResult(
  type: string,
  kind: string,
  internalId: string | undefined,
  externalId: string | undefined,
  state: 'success' | 'failed' | 'skipped' = 'success',
  isDryRun = false,
): string {
  const entity = type.charAt(0).toUpperCase() + type.slice(1);
  const id = internalId ?? externalId;

  const [verbTo, verbIng, action] =
    kind === 'create'
      ? ['create', 'creating', 'Created']
      : kind === 'update'
        ? ['update', 'updating', 'Updated']
        : ['delete', 'deleting', 'Deleted'];

  const prefix =
    state === 'failed'
      ? `Failed to ${verbTo} ${entity}`
      : state === 'skipped'
        ? `Skipped ${verbIng} ${entity}`
        : isDryRun
          ? `Would ${verbTo} ${entity}`
          : `${action} ${entity}`;

  const suffix = kind === 'create' ? `with external ID ${externalId}` : `with ID ${internalId}`;

  return id ? `${prefix} ${suffix}` : prefix;
}

/**
 * Display-label constants for Edlink sync delta details.
 * Used in both the server (detailsForDelta) and client (normalizeDryRunDelta)
 */
export const EDLINK_DELTA_LABELS = {
  studentProfileStatus: 'studentProfile.status',
  studentProfileFleid: 'studentProfile.fleid',
  schoolAdded: 'schoolId (added)',
  schoolRemoved: 'schoolId (removed)',
} as const;

/**
 * A derived delta representing a student gaining or losing a school membership.
 * Encodes information that is present in {@link UserRoleDelta}s but not easy to
 * read as "school change" without calculation.
 */
export interface StudentSchoolCreation {
  delta: 'user.school';
  kind: 'create';
  userId: string;
  schoolId: string;
  schoolName?: string;
}

export interface StudentSchoolDeletion {
  delta: 'user.school';
  kind: 'delete';
  userId: string;
  schoolId: string;
  schoolName?: string;
}

export type StudentSchoolDelta = StudentSchoolCreation | StudentSchoolDeletion;

/**
 * Derives student-school membership deltas from a user's role deltas.
 */
export function schoolRoleChangesFromRoleDeltas(
  userId: string,
  roleDeltas: UserRoleDelta<string>[],
): StudentSchoolDelta[] {
  const created: StudentSchoolCreation[] = roleDeltas
    .filter((r): r is UserRoleCreationDelta<string> => r.kind === 'create' && r.data.role === 'student')
    .flatMap((r) => {
      const schoolId = r.data.schoolId;
      if (schoolId == null) return [];
      return [
        {
          delta: 'user.school' as const,
          kind: 'create' as const,
          userId,
          schoolId: typeof schoolId === 'string' ? schoolId : schoolId.externalId,
          schoolName: r.schoolName,
        },
      ];
    });

  const deleted: StudentSchoolDeletion[] = roleDeltas
    .filter(
      (r): r is UserRoleDeletionDelta => r.kind === 'delete' && r.match.role === 'student' && r.match.schoolId != null,
    )
    .map((r) => ({
      delta: 'user.school' as const,
      kind: 'delete' as const,
      userId,
      schoolId: String(r.match.schoolId),
      schoolName: r.schoolName,
    }));

  return [...created, ...deleted];
}

/**
 * Formats {@link StudentSchoolDelta}s into display details for sync summaries.
 */
export function detailsFromStudentSchoolDeltas(deltas: StudentSchoolDelta[]): Record<string, string> {
  const { schoolAdded: schoolAddedLabel, schoolRemoved: schoolRemovedLabel } = EDLINK_DELTA_LABELS;

  const addedSchools = deltas
    .filter((d): d is StudentSchoolCreation => d.kind === 'create')
    .map((d) => d.schoolName ?? d.schoolId)
    .join(', ');

  const removedSchools = deltas
    .filter((d): d is StudentSchoolDeletion => d.kind === 'delete')
    .map((d) => d.schoolName ?? d.schoolId)
    .join(', ');

  return {
    ...(addedSchools ? { [schoolAddedLabel]: addedSchools } : {}),
    ...(removedSchools ? { [schoolRemovedLabel]: removedSchools } : {}),
  };
}
